import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";

const ResidencyFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  mode = "create",
  initialData = null,
}) => {
  const [form, setForm] = useState({
    name: "",
    status: "draft",
    flyer: null,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      setForm({
        name: initialData.name || "",
        status: initialData.status || "draft",
        flyer: initialData.flyer || null,
      });
    } else {
      setForm({ name: "", status: "draft", flyer: null });
    }
    setError("");
  }, [isOpen, initialData]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleFlyerChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const resetInput = () => { e.target.value = null; };

    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      setError("Only JPG, JPEG and PNG files are allowed.");
      resetInput();
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== 1920 || img.height !== 1080) {
        handleChange("flyer", null);
        setError("Image must be exactly 1920 × 1080 pixels.");
        resetInput();
        URL.revokeObjectURL(objectUrl);
        return;
      }
      setError("");
      handleChange("flyer", file);
      URL.revokeObjectURL(objectUrl);
    };

    img.onerror = () => {
      setError("Invalid image file.");
      resetInput();
      URL.revokeObjectURL(objectUrl);
    };

    img.src = objectUrl;
  };

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setError("Please enter a residency name.");
      return;
    }
    if (mode === "create" && !form.flyer) {
      setError("Please upload a flyer image.");
      return;
    }
    onSubmit(form);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {mode === "edit" ? "Edit Residency" : "Create Residency"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-5 max-h-[70vh] overflow-y-auto">

          {/* Name */}
          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Residency Name</label>
            <input
              type="text"
              value={form.name}
              placeholder="e.g. Afrodise Summer Series"
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40 placeholder-slate-500"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm text-slate-300 mb-1.5">Status</label>
            <select
              value={form.status}
              onChange={(e) => handleChange("status", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="finished">Finished</option>
            </select>
          </div>

          {/* Flyer Upload */}
          <div>
            <label className="block text-sm text-slate-300 mb-1.5">
              Flyer — Horizontal (1920 × 1080) · JPG, JPEG, PNG
            </label>

            {/* Upload area */}
            <label
              htmlFor="flyer-upload"
              className={`flex flex-col items-center justify-center w-full h-30 rounded-xl border-2 border-dashed cursor-pointer transition
                ${form.flyer
                  ? "border-pink-500/30 bg-pink-500/5"
                  : "border-white/10 hover:border-pink-500/30 hover:bg-pink-500/5 text-slate-500 hover:text-slate-300"
                }`}
            >
              {form.flyer ? (
                <div className="flex items-center gap-2 px-4">
                  <svg className="w-5 h-5 text-pink-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm text-pink-400 truncate max-w-[280px]">
                    {typeof form.flyer === "string" ? "Current flyer" : form.flyer.name}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); handleChange("flyer", null); }}
                    className="ml-auto text-slate-500 hover:text-white transition flex-shrink-0"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <>
                  <svg className="w-6 h-6 mt-2 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span className="text-sm">Click to upload flyer</span>
                  <span className="text-xs mt-1 text-slate-600 mb-2">1920 × 1080 required</span>
                </>
              )}
            </label>
            <input
              id="flyer-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              className="hidden"
              onChange={handleFlyerChange}
            />

            {/* Preview */}
            {form.flyer && (
              <div className="mt-3 rounded-lg overflow-hidden border border-white/10">
                <img
                  src={typeof form.flyer === "string" ? form.flyer : URL.createObjectURL(form.flyer)}
                  alt="Flyer preview"
                  className="w-full h-40 object-contain"
                />
              </div>
            )}
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2.5 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg text-white font-medium bg-pink-600 hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : mode === "edit" ? "Update Residency" : "Create Residency"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResidencyFormModal;